import * as React from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { animated, useSpring } from 'react-spring';

import { useQuery } from '@apollo/client';
import { GET_PROPERTY_TOP_QUERY } from '../../../../../client/__graphql__/queries/getPropertyTop';
import {
  GetPropertyTop,
  GetPropertyTopVariables,
} from '../../../../__types__/generated/GetPropertyTop';

import { OpenGalleryButton } from '../ui/OpenGalleryButton';

import { SiteContext } from '../../../utils/context/SiteContext';
import { Button } from '../../../ui/Button';

import { Gallery } from '../ui/gallery/Index';
import { SoldPriceForm } from '../ui/SoldpriceForm';

export const Top: React.FC = React.memo(() => {
  const params = useParams<{ sguid: string; page?: string }>();
  const { galleryOpen, setGalleryOpen } = React.useContext(SiteContext);

  const { data, loading } = useQuery<GetPropertyTop, GetPropertyTopVariables>(
    GET_PROPERTY_TOP_QUERY,
    {
      fetchPolicy: 'cache-only',
      variables:
        params?.page === 'preview'
          ? { input: { sguid: params.sguid, preview: true } }
          : { input: { sguid: params.sguid } },
    }
  );
  data?.getProperty?.description;

  if (!data || loading) {
    return null;
  }

  const animatedDescProps = useSpring({
    from: { transform: 'translate3d(0%, 0, 0)', display: 'flex' },
    to: {
      transform: galleryOpen
        ? 'translate3d(100%, 0, 0)'
        : 'translate3d(0%, 0, 0)',
      display: galleryOpen ? 'none' : 'flex',
    },
  });

  const animatedDescInsideProps = useSpring({
    delay: !galleryOpen ? 500 : 0,
    from: { opacity: 1 },
    to: {
      opacity: !galleryOpen && data.getProperty?.description ? 1 : 0,
    },
  });

  return (
    <StyledTop>
      <GallerySection data={data} />
      <TopDescription style={animatedDescProps}>
        {data.getProperty?.sold ? (
          <div className="row">
            <animated.div className="col" style={animatedDescInsideProps}>
              <SoldPriceForm data={data} />
            </animated.div>
            <div className="col">
              <OpenGalleryButton onClick={() => setGalleryOpen(true)} />
            </div>
          </div>
        ) : (
          <div className="row">
            <animated.div className="col" style={animatedDescInsideProps}>
              {data?.getProperty?.projectLogos?.list &&
              data?.getProperty?.projectLogos?.list?.length > 0 ? (
                <StyledLogoSection>
                  {data.getProperty?.projectLogos?.list.map((item) => {
                    let styles: any = {};
                    if (item?.width) {
                      styles.width = `${item.width}px`;
                    }
                    if (item?.height) {
                      styles.height = `${item.height}px`;
                    }
                    return (
                      <div className="developer-logo" key={`logo_${item?.id}`}>
                        <img
                          alt="logo til utbygger"
                          src={item?.url || ''}
                          style={styles}
                        />
                      </div>
                    );
                  })}
                </StyledLogoSection>
              ) : null}
              <h1 className="property-title">
                {data?.getProperty?.description}
              </h1>
              <p className="property-description ingress">
                Som kunde hos PrivatMegleren får du en skreddersydd prosess
                tilpasset dine behov og ønsker
              </p>
              {data.getProperty?.viewing3d?.info?.url ? (
                <ButtonGrid style={{ marginTop: '20px' }}>
                  <Button
                    variant="solid"
                    colorVariant="primary"
                    onClick={() => {
                      const link = data.getProperty?.viewing3d?.info?.url;
                      if (link) {
                        window.open(link, '_blank');
                      }
                    }}
                  >
                    3D-visning
                  </Button>
                </ButtonGrid>
              ) : null}
              {data?.getProperty?.links?.list
                ?.filter((item) => item?.name === 'Utbyggers hjemmeside')
                .map((item) => {
                  return (
                    <ButtonGrid
                      style={{ marginTop: '20px' }}
                      key={`button_${item?.id}`}
                    >
                      <Button
                        variant="solid"
                        colorVariant="primary"
                        onClick={() => {
                          if (item?.url) {
                            window.open(item?.url, '_blank');
                          }
                        }}
                      >
                        Utbyggers hjemmeside
                      </Button>
                    </ButtonGrid>
                  );
                })}
              {data?.getProperty?.links?.list
                ?.filter((item) =>
                  item?.name?.match(
                    /(prosjektetshjemmeside|prosjektets hjemmeside)/i
                  )
                )
                .map((item) => {
                  return (
                    <ButtonGrid
                      style={{ marginTop: '20px' }}
                      key={`button_${item?.id}`}
                    >
                      <Button
                        variant="solid"
                        colorVariant="primary"
                        onClick={() => {
                          if (item?.url) {
                            window.open(item?.url, '_blank');
                          }
                        }}
                      >
                        Prosjektets hjemmeside
                      </Button>
                    </ButtonGrid>
                  );
                })}
            </animated.div>
            <div className="col">
              <OpenGalleryButton onClick={() => setGalleryOpen(true)} />
            </div>
          </div>
        )}
      </TopDescription>
    </StyledTop>
  );
});

const StyledTop = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  overflow: hidden;
`;

const GallerySection: React.FC<{ data: GetPropertyTop }> = React.memo(
  ({ data }) => {
    const params = useParams<{ sguid: string }>();
    const { galleryOpen, setGalleryOpen } = React.useContext(SiteContext);

    const animatedProps = useSpring({
      from: { width: '50%' },
      to: {
        width: galleryOpen ? '100%' : '50%',
      },
    });

    const getVideoLink = React.useCallback(() => {
      let url = null;
      if (
        data?.getProperty?.links?.list &&
        data?.getProperty?.links?.list?.length > 0
      ) {
        const link = data?.getProperty?.links?.list.filter((item) =>
          item?.name?.match(/finn.no-annonsevideo/gi)
        )[0];

        if (link?.url) {
          url = link.url;
        }
      }
      return { url };
    }, [data?.getProperty?.links?.list]);

    const videoLink = getVideoLink();

    return (
      <TopGalleryWrapper
        className={galleryOpen ? 'gallery-open' : ''}
        style={animatedProps}
      >
        {!galleryOpen && data?.getProperty?.sold ? (
          <Sold onClick={() => setGalleryOpen(true)} />
        ) : null}
        {!galleryOpen ? (
          <TopGalleryNav>
            <div className="nav-top">
              {data?.getProperty?.newDevelopment?.showPriceList ||
              data.getProperty?.newDevelopment?.estatePicker?.url ? (
                <div className={'top single'}>
                  <Button
                    variant="solid"
                    colorVariant="primary"
                    onClick={() => {
                      window.open(
                        `https://privatmegleren.no/prisliste/${params.sguid}`
                      );
                    }}
                  >
                    Prisliste
                  </Button>
                  {data.getProperty?.newDevelopment?.estatePicker?.url &&
                  data.getProperty?.newDevelopment?.estatePicker?.label ? (
                    <Button
                      variant="solid"
                      colorVariant="primary"
                      onClick={() => {
                        const link =
                          data.getProperty?.newDevelopment?.estatePicker?.url;
                        if (link) {
                          window.open(link, '_blank');
                        }
                      }}
                    >
                      {data.getProperty?.newDevelopment?.estatePicker?.label}
                    </Button>
                  ) : null}
                  {data?.getProperty?.hovedoppdrag !== -1 ? (
                    <Button
                      variant="solid"
                      colorVariant="primary"
                      onClick={() => {
                        if (data?.getProperty?.bid?.link?.url) {
                          window.open(data.getProperty?.bid?.link?.url);
                        }
                      }}
                    >
                      Kjøp
                    </Button>
                  ) : null}
                </div>
              ) : null}
              {videoLink?.url ? (
                <div className="bottom">
                  <Button
                    variant="solid"
                    colorVariant="primary"
                    onClick={() => {
                      if (videoLink?.url) {
                        window.open(videoLink.url, '_blank');
                      }
                    }}
                  >
                    Video
                  </Button>
                </div>
              ) : null}
              {data?.getProperty?.links?.list
                ?.filter((item) => item?.name === 'Utbyggers hjemmeside')
                .map((item) => {
                  return (
                    <div className="bottom mobile" key={`button_${item?.id}`}>
                      <Button
                        variant="solid"
                        colorVariant="primary"
                        onClick={() => {
                          if (item?.url) {
                            window.open(item?.url, '_blank');
                          }
                        }}
                      >
                        Utbyggers hjemmeside
                      </Button>
                    </div>
                  );
                })}

              {data?.getProperty?.links?.list
                ?.filter((item) =>
                  item?.name?.match(
                    /(prosjektetshjemmeside|prosjektets hjemmeside)/i
                  )
                )
                .map((item) => {
                  return (
                    <div className="bottom mobile" key={`button_${item?.id}`}>
                      <Button
                        variant="solid"
                        colorVariant="primary"
                        onClick={() => {
                          if (item?.url) {
                            window.open(item?.url, '_blank');
                          }
                        }}
                      >
                        Prosjektets hjemmeside
                      </Button>
                    </div>
                  );
                })}
            </div>
          </TopGalleryNav>
        ) : null}
        {!galleryOpen ? (
          <TopGalleryContentMobile>
            {!data?.getProperty?.sold ? (
              <div className="content" onClick={() => setGalleryOpen(true)}>
                <span>Klikk for galleri</span>
                <h1>{data?.getProperty?.description}</h1>
              </div>
            ) : null}
          </TopGalleryContentMobile>
        ) : null}
        <Gallery data={data} />
      </TopGalleryWrapper>
    );
  }
);

const TopGalleryNav = styled.nav`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: block;
  padding: 0 30px;
  z-index: 3;
  display: flex;
  flex-flow: column;
  justify-content: flex-end;

  .nav-top {
    display: flex;
    flex-flow: column;
    width: 100%;
    margin: 0 auto 60px auto;
    max-width: 420px;
    justify-content: center;

    .top {
      display: grid;
      grid-template-columns: 1fr;
      grid-row-gap: 20px;
      margin-bottom: 20px;
      width: 100%;
    }
    .bottom {
      display: flex;
      width: 100%;
    }

    @media all and (max-width: 960px) {
      margin-bottom: 30px;
    }
  }
  .nav-bottom {
    display: grid;
    grid-template-columns: 1fr 1fr;
    justify-content: center;

    @media all and (max-width: 960px) {
      display: none;
    }
  }
`;

const TopGalleryContentMobile = styled.div`
  display: none;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0 30px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.6);

  .content {
    display: flex;
    flex-flow: column;
    justify-content: center;

    span {
      font-size: 0.7em;
      letter-spacing: 2px;
      color: rgb(255, 255, 255);
      text-align: center;
    }
    h1 {
      margin-top: 10px;
      text-align: center;
      font-size: 28px;
      color: #fff;
    }
  }

  &:hover {
    cursor: pointer;
  }

  @media all and (max-width: 960px) {
    display: flex;
  }
`;

const TopGalleryWrapper = styled(animated.div)`
  position: relative;
  width: 50%;
  height: 100vh;
  background: rgb(10, 10, 10);

  @media all and (max-width: 960px) {
    width: 100% !important;
    max-height: 800px;
    overflow: hidden;

    &.gallery-open {
      max-height: initial;
    }
  }
`;

const TopDescription = styled(animated.div)`
  display: flex;
  justify-content: center;
  flex-flow: column;
  width: 50%;
  height: 100vh;
  padding: 0 4em;

  .row {
    display: flex;
    width: 100%;

    .col {
      padding: 0 4em 0 0;
      display: flex;
      flex-flow: column;
      justify-content: center;

      &:last-child {
        padding: 0;
      }
    }
  }

  .property-title {
    font-size: 2.2em;
    color: ${({ theme }) => theme.top.heading};
  }
  .property-sold-description {
    font-size: 1.1em;
  }
  .property-description {
    color: ${({ theme }) => theme.top.text};
  }

  @media all and (max-width: 960px) {
    display: none !important;
  }
`;

const StyledLogoSection = styled.div`
  width: 100%;

  .developer-logo {
    margin-bottom: 20px;
  }
`;

const Sold = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20vw;
  height: 20vw;
  background: url('https://cdn.reeltime.no/pm_assets/img/solgt.png') 100%/100%;
  transform: translate(-50%, -50%);
  z-index: 99;

  @media all and (max-width: 960px) {
    width: 225px;
    height: 225px;
  }
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: max-content;
`;
